import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronDown, Search, MessageCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import Footer from "../components/Footer";

const FAQS = [
  { cat: "Getting Started", q: "Is BTR Health free for participants?", a: "Yes. We charge zero participant fees. There are no sign-up, booking, or subscription fees for participants. Workers pay a small platform fee from their hourly earnings." },
  { cat: "Getting Started", q: "Do I need an NDIS plan to use BTR Health?", a: "Yes — we connect NDIS participants with NDIS-registered support workers. You can sign up before your plan is finalised and start browsing workers, but bookings require an active plan." },
  { cat: "Getting Started", q: "How long does sign-up take?", a: "Participants can sign up in under 5 minutes. Workers complete a profile + document upload and are typically verified within 1–2 business days." },
  { cat: "Booking", q: "How do I book a shift?", a: "Search for a worker, message them to confirm availability, then click 'Book' on their profile. Or post an open shift and have matched workers in your area come to you." },
  { cat: "Booking", q: "What's your cancellation policy?", a: "Cancellations 24+ hours before a shift are free. Within 24 hours, the worker is paid 100% of the agreed shift to honor their time. We don't charge participants any platform cancellation fee." },
  { cat: "Booking", q: "Can I book the same worker repeatedly?", a: "Absolutely. Once you find a worker you like, your dashboard surfaces them in 'Book Again'. We support recurring shifts as well." },
  { cat: "Payments", q: "How do invoices work?", a: "After every shift the worker submits a timesheet, you approve it, and we instantly generate an NDIS-aligned invoice. Self-Managed: a Stripe payment link is emailed to you. Plan-Managed: the invoice is emailed to your plan manager. NDIA-Managed: it's flagged for processing in MyPlace." },
  { cat: "Payments", q: "Are NDIS supports GST-free?", a: "Yes. Supports delivered under the NDIS are GST-free under the GST Act (Section 38-38). Our invoices reflect this." },
  { cat: "Payments", q: "What happens if I dispute a timesheet?", a: "Click 'Dispute' on the timesheet. We pause invoice generation, message the worker, and a BTR Health admin mediates within 1 business day." },
  { cat: "Workers", q: "Are workers verified?", a: "Every worker uploads their NDIS Worker Screening Clearance, National Police Check, First Aid, photo ID, and (for independents) Public Liability Insurance. We manually review each one before approving the worker." },
  { cat: "Workers", q: "What's the difference between Independent and Employed workers?", a: "Independent (sole trader) workers run their own ABN business and set their own rate. Employed workers are PAYG-employed by BTR Health under a written contract with super, leave and tax handled by us." },
  { cat: "Safety", q: "What happens if I feel unsafe during a shift?", a: "Stop the activity, call 000 if it's a life-threatening emergency, then lodge an incident report in-app or email safety@btrhealth.com.au. We respond within 30 minutes." },
  { cat: "Safety", q: "Do workers carry insurance?", a: "Independent workers carry Public Liability Insurance (uploaded as part of verification). Employed workers are covered by BTR Health's master policy." },
  { cat: "Account", q: "How do I delete my account?", a: "Email hello@btrhealth.com.au and we'll delete your account and personal data within 7 days, except where we're legally required to retain records (e.g. financial records for 7 years)." }
];

const CATS = Array.from(new Set(FAQS.map(f => f.cat)));

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-slate-100">
      <button onClick={() => setOpen(!open)} className="w-full text-left py-5 flex items-start justify-between gap-4 hover:bg-slate-50 transition-colors px-2 rounded-md" data-testid="faq-toggle">
        <span className="font-medium text-slate-900">{q}</span>
        <ChevronDown className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <motion.div initial={false} animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }} className="overflow-hidden">
        <p className="text-slate-600 pb-5 px-2 leading-relaxed">{a}</p>
      </motion.div>
    </div>
  );
}

export default function HelpCenterPage() {
  const [query, setQuery] = useState("");
  const filtered = FAQS.filter(f => (f.q + " " + f.a).toLowerCase().includes(query.toLowerCase()));

  return (
    <div className="min-h-screen bg-white">
      <header className="container mx-auto px-4 py-5 flex items-center justify-between max-w-6xl">
        <Link to="/" className="font-bold text-xl text-slate-900">BTR Health</Link>
        <div className="flex gap-3">
          <Link to="/"><Button variant="outline" className="rounded-full">Home</Button></Link>
          <Link to="/register"><Button className="bg-[#87CEEB] hover:bg-[#6BB8DB] rounded-full">Get started</Button></Link>
        </div>
      </header>

      <section className="container mx-auto px-4 py-14 max-w-3xl text-center">
        <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-4xl md:text-6xl font-bold text-slate-900 mb-5">Help center</motion.h1>
        <p className="text-lg text-slate-600 mb-8">Real answers from a real Australian team.</p>
        <div className="relative max-w-xl mx-auto">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            placeholder="Search the help center…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-12 h-12 rounded-full text-base"
            data-testid="help-search"
          />
        </div>
      </section>

      <section className="container mx-auto px-4 max-w-3xl mb-16">
        {CATS.map(cat => {
          const items = filtered.filter(f => f.cat === cat);
          if (items.length === 0) return null;
          return (
            <div key={cat} className="mb-10">
              <h2 className="text-xl font-bold text-slate-900 mb-3">{cat}</h2>
              <div className="bg-white rounded-2xl border border-slate-100">
                {items.map(f => <FaqItem key={f.q} q={f.q} a={f.a} />)}
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <p className="text-center text-slate-500 py-10">No results — try a different search term, or <a className="text-[#87CEEB] underline" href="mailto:hello@btrhealth.com.au">email us</a>.</p>
        )}
      </section>

      <section className="bg-slate-50 py-12">
        <div className="container mx-auto px-4 max-w-3xl text-center">
          <MessageCircle className="w-10 h-10 text-[#87CEEB] mx-auto mb-3" />
          <h3 className="text-xl font-bold text-slate-900 mb-2">Still need help?</h3>
          <p className="text-slate-600 mb-5">Our Sydney-based team replies within 1 business day.</p>
          <a href="mailto:hello@btrhealth.com.au"><Button className="bg-[#87CEEB] hover:bg-[#6BB8DB] rounded-full px-6">Email hello@btrhealth.com.au</Button></a>
        </div>
      </section>
      <Footer />
    </div>
  );
}
